import { Fragment, ReactNode } from 'react';
import { OpenModal, TOpenModal } from './OpenModal';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react-lite';

export class ApiaUtilModalHandler {
  constructor(
    public close: () => void,
    public setProperties: (props: Partial<TOpenModal>) => unknown,
    public getProperties: () => TOpenModal,
  ) {}

  public setConfirmProps(props: Partial<TOpenModal['confirmProps']>) {
    this.setProperties({
      confirmProps: { ...this.getProperties().confirmProps, ...props },
    });
  }
}

export class ApiaUtilModals {
  private overlays: { render: ReactNode; id: number }[] = [];
  private modals: (TOpenModal & { isOpen: boolean })[] = [];

  #maxId = 0;
  #maxOverlay = 0;

  constructor() {
    document.addEventListener('openModal', (event) => {
      const cevent = event as CustomEvent;
      this.open(cevent.detail as TOpenModal);
    });

    makeObservable<ApiaUtilModals, 'overlays' | 'modals'>(this, {
      overlays: observable,
      modals: observable,
    });
  }

  #innerClose = (id: string) => {
    this.modals = this.modals.filter((s) => s.id !== id);
  };

  close(id: string) {
    const modal = this.modals.find((s) => s.id === id);

    if (modal) {
      modal.onClose?.();
      modal.onExited?.();
    }

    this.#innerClose(id);
  }

  open(props: TOpenModal) {
    const id = props.id ?? `modal_${this.#maxId++}`;
    this.modals.push({
      ...props,
      id,
      isOpen: true,
      onClose: props.onClose,
      onExited: () => {
        props.onExited?.();
        this.#innerClose(id);
      },
    });

    return new ApiaUtilModalHandler(
      () => {
        this.close(id);
      },
      (props) => {
        Object.assign(this.modals.find((m) => m.id === id) ?? {}, props);
      },
      () => {
        return this.modals.find((m) => m.id === id)!;
      },
    );
  }

  /**
   * The overlay method allows to append an element to the array of overlays, which will be rendered after any existent modal.
   *
   * This way, it's possible to add a completelly custom modal with no style, nor any custom behavior added by this controller.
   * 
   * @example
   * 
   *  const portal = createPortal(
    <iframe
      style={{
        position: 'fixed',
        inset: 0,
      }}
      src={makeApiaUrl({
        action: 'editGrid',
        frmName: grid.getProperty('gridForm'),
        frmParent: grid.getForm().definition.frmParent,
        portalFormMode: false,
        editionMode: true,
        react: true,
        index,
        preventAsXmlParameter: true,
        ajaxUrl: 'apia.execution.FormAction.run',
      })}
    />,
    document.getElementById('AppModals') as HTMLElement,
  );

  ApiaUtil.instance.modals.overlay(portal);
   */
  overlay(o: ReactNode) {
    const id = this.#maxOverlay++;

    const overlay = { render: <Fragment key={id}>{o}</Fragment>, id };
    this.overlays.push(overlay);

    return () => {
      this.overlays = this.overlays.filter((c) => c.id !== id);
    };
  }

  Component = observer(() => {
    return (
      <>
        {this.modals.map((current) => (
          <OpenModal key={current.id} {...current} />
        ))}
        {this.overlays.map((c) => c.render)}
      </>
    );
  });
}
